import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../utils/fetchJson';
import useAyPhaseReady from '../../../utils/useAyPhaseReady';
import useCurrentUser from '../../../utils/useCurrentUser';
import { useRef } from 'react';
import UserForm from '../../../components/School_Admin/StaffManagement/UserForm';
// Emitted from the real function in @ameca/core rather than retyped, so the
// wording cannot drift from the phone's. See pageCatalogue.generated.js.
import { levelLabel } from '../../../config/pageCatalogue.generated';

const UserFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { ready, activePhase, schoolId } = useAyPhaseReady();
  const { user } = useCurrentUser();
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const isEditing = !!location.state?.userId;
  const userId = location.state?.userId;

  const [activeStep, setActiveStep] = useState(0);
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phone: '',
    roleId: '',
    status: 'active'
  });
  const [roles, setRoles] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [teacherAllocationType, setTeacherAllocationType] = useState('one_class');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [selectedClassIdForStudents, setSelectedClassIdForStudents] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const stepsBase = useMemo(() => [
    t('users.stepProfileRole') || 'Profile & Role', 
    t('users.stepReview') || 'Review'
  ], [t]);

  const selectedRole = useMemo(() => {
    return roles.find(r => String(r.id) === String(userData.roleId)) || null;
  }, [roles, userData.roleId]);

  const isParentRole = useMemo(() => {
    return (selectedRole?.name || '').toLowerCase().replace(/\s+/g, '') === 'parent';
  }, [selectedRole]);

  const isTeachingRole = useMemo(() => {
    const name = (selectedRole?.name || '').toLowerCase();
    return ['teacher', 'classmaster', 'class master', 'subjecthead', 'subject head'].some(k => name.includes(k));
  }, [selectedRole]);

  const isTeacherRole = useMemo(() => {
    const name = (selectedRole?.name || '').toLowerCase().trim().replace(/\s+/g, '');
    return name === 'teacher';
  }, [selectedRole]);

  const isNurPriPhase = useMemo(() => {
    return String(activePhase || '').toLowerCase() === 'nur_pri';
  }, [activePhase]);

  const isNurPriTeacher = useMemo(() => {
    return isNurPriPhase && isTeacherRole;
  }, [isNurPriPhase, isTeacherRole]);

  const shouldLoadClassesForTeacher = useMemo(() => {
    return isNurPriTeacher && teacherAllocationType === 'one_class';
  }, [isNurPriTeacher, teacherAllocationType]);

  const steps = useMemo(() => {
    if (isTeachingRole || isParentRole) {
      return [
        ...stepsBase.slice(0, 1),
        (t('users.stepAllocations') || 'Allocations'),
        ...stepsBase.slice(1)
      ];
    }
    return stepsBase;
  }, [isTeachingRole, isParentRole, stepsBase, t]);

  const visibleClasses = useMemo(() => {
    return classes;
  }, [classes]);

  const showClassAllocations = useMemo(() => {
    // SEC teachers: classes are auto-derived from subjects, no manual class selection needed
    // Only NUR_PRI one-class teachers need class allocation (handled by shouldLoadClassesForTeacher)
    return false;
  }, []);

  useEffect(() => {
    if (!isNurPriTeacher) return;
    if (teacherAllocationType === 'one_class' && selectedSubjectIds.length) {
      setSelectedSubjectIds([]);
    }
    if (teacherAllocationType === 'subjects' && selectedClassIds.length) {
      setSelectedClassIds([]);
    }
  }, [teacherAllocationType, isNurPriTeacher, selectedSubjectIds.length, selectedClassIds.length]);

  useEffect(() => {
    const loadStudents = async () => {
      if (!isParentRole) return;
      try {
        setLoadingStudents(true);
        const activeSchoolId = String(schoolId || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '');
        // ALL SECTIONS, for a parent only.
        //
        // A guardian is a person and their children can be in either section —
        // `guardians` carries no section at all, by design. This list is
        // section-filtered like every other student list, so an admin could not
        // link a parent to a child in the other section without switching, and
        // switching to do it is what used to delete the first child's link.
        //
        // `bypassPhase` is the escape hatch studentController already provides.
        const data = await fetchJson(`/api/students?schoolId=${activeSchoolId}&bypassPhase=1`);
        const studentList = Array.isArray(data) ? data : (data?.rows || data?.data || []);
        setAllStudents(studentList);
        setStudents(studentList);
      } catch (e) {
        console.warn('Failed to load students for parent linking:', e);
      } finally {
        setLoadingStudents(false);
      }
    };
    loadStudents();
  }, [isParentRole, schoolId]);

  useEffect(() => {
    if (selectedClassIdForStudents) {
      setStudents(allStudents.filter(s => String(s.classId || s.Class?.id) === String(selectedClassIdForStudents)));
    } else {
      setStudents(allStudents);
    }
  }, [selectedClassIdForStudents, allStudents]);

  useEffect(() => {
    const loadClasses = async () => {
      if (!isParentRole || classes.length > 0) return;
      try {
        const data = await fetchJson('/api/allocations/classes');
        setClasses(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn('Failed to load classes for parent filtering:', e);
      }
    };
    loadClasses();
  }, [isParentRole, classes.length]);

  useEffect(() => {
    const ctrl = new AbortController();
    const load = async () => {
      try {
        setLoading(true);
        const activeSchoolId = String(schoolId || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '');
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        
        const cacheKey = `assignable_roles_cache_v11_${activeSchoolId || 'no_school'}_${(activePhase || '').toLowerCase()}`;
        const cached = sessionStorage.getItem(cacheKey);
        let assignableRoles = null;
        
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed && parsed.expiresAt && parsed.expiresAt > Date.now()) {
              const data = parsed.data;
              const looksValid = Array.isArray(data)
                && data.every((r) => r && Object.prototype.hasOwnProperty.call(r, 'isTemplate') && Object.prototype.hasOwnProperty.call(r, 'isSystemRole'))
                && data.some((r) => r && r.schoolId == null);
              if (looksValid) assignableRoles = data;
            }
          } catch {}
        }

        if (!assignableRoles) {
          const rolesData = await fetchJson('/api/roles/assignable', {
            signal: ctrl.signal,
            headers: {
              ...(activeSchoolId ? { 'X-Scope-School-Id': activeSchoolId } : {}),
              ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {})
            }
          });
          const base = Array.isArray(rolesData) ? rolesData : (rolesData?.data || rolesData?.roles || []);
          assignableRoles = base
            .map(r => ({
              id: r.id,
              name: r.name,
              description: r.description || '',
              permissions: Array.isArray(r.permissions)
                ? r.permissions
                : (Array.isArray(r.Permissions)
                    ? r.Permissions.map(p => p?.name).filter(Boolean)
                    : null),
              isSystemRole: r.isSystemRole,
              isTemplate: r.isTemplate,
              metadata: r.metadata || {},
              schoolId: r.schoolId || null
            }))
            .sort((a,b) => a.name.localeCompare(b.name));

          assignableRoles = assignableRoles.filter((r) => {
            if (r.schoolId == null) return !!r.isTemplate || !!r.isSystemRole;
            if (!activeSchoolId) return true;
            return String(r.schoolId) === String(activeSchoolId);
          });

          const phase = (activePhase || '').toLowerCase();
          if (phase) {
            assignableRoles = assignableRoles.filter(r => {
              const metaPhase = r.metadata && r.metadata.phaseCode
                ? String(r.metadata.phaseCode).toLowerCase()
                : null;
              if (metaPhase && metaPhase !== phase) return false;
              const nmSlug = (r.name || '').toLowerCase().replace(/\s+/g,'');
              if (phase === 'sec' && nmSlug === 'headmaster') return false;
              if (phase === 'nur_pri' && nmSlug === 'principal') return false;
              return true;
            });
          }

          assignableRoles = assignableRoles.filter((r) => {
            const nmSlug = (r?.name || '').toString().toLowerCase().replace(/\s+/g, '');
            return nmSlug !== 'student' && nmSlug !== 'systemadmin';
          });

          sessionStorage.setItem(cacheKey, JSON.stringify({ data: assignableRoles, expiresAt: Date.now() + 5 * 60 * 1000 }));
        }

        // Enforce hierarchy: only SchoolAdmin can see/assign SchoolAdmin.
        // Applied OUTSIDE the cache block so it always reflects the current viewer's role,
        // even when roles were served from the session cache.
        const viewerRoleNorm = (userRef.current?.roleNormalized || userRef.current?.role || userRef.current?.Role?.name || '').toString().toLowerCase().replace(/\s+/g,'').replace(/[^a-z]/g, '');
        const viewerIsSchoolAdmin = viewerRoleNorm === 'schooladmin';
        if (!viewerIsSchoolAdmin) {
          assignableRoles = assignableRoles.filter((r) => {
            const nmSlug = (r?.name || '').toString().toLowerCase().replace(/\s+/g, '');
            return nmSlug !== 'schooladmin';
          });
        }

        setRoles(assignableRoles);

        if (isEditing) {
          const userResp = await fetchJson(`/api/users/${userId}`, {
            signal: ctrl.signal,
            headers: {
              ...(activeSchoolId ? { 'X-Scope-School-Id': activeSchoolId } : {}),
              ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {})
            }
          });
          setUserData(userResp);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          enqueueSnackbar(t('users.errorFetch'), { variant: 'error' });
          console.error('Failed to fetch user data:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => ctrl.abort();
  }, [userId, isEditing, t, enqueueSnackbar, activePhase, schoolId]); // 'user' intentionally omitted: only needed for SchoolAdmin filtering, accessed via ref to avoid spurious re-fetches

  useEffect(() => {
    const activeSchoolId = String(schoolId || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '');
    const onPhase = () => sessionStorage.removeItem(`assignable_roles_cache_v11_${activeSchoolId || 'no_school'}_${(activePhase || '').toLowerCase()}`);
    const onAy = () => sessionStorage.removeItem(`assignable_roles_cache_v11_${activeSchoolId || 'no_school'}_${(activePhase || '').toLowerCase()}`);
    window.addEventListener('app:phase:changed', onPhase);
    window.addEventListener('app:ay:changed', onAy);
    return () => {
      window.removeEventListener('app:phase:changed', onPhase);
      window.removeEventListener('app:ay:changed', onAy);
    };
  }, [activePhase, schoolId]);

  // Pre-populate existing allocations when editing a teaching or parent user.
  // Must wait until userData.roleId is set (user fetch complete) AND role is resolved.
  useEffect(() => {
    if (!isEditing || !userId || !userData.roleId) return;
    if (!isTeachingRole && !isParentRole) return;
    let cancelled = false;
    const loadExistingAllocations = async () => {
      try {
        if (isTeachingRole) {
          // THE CLASS COMES FROM THE CLASS LIST, NOT FROM TeacherSubject.
          //
          // This used to read `a.classId` off /api/teacher-subjects/teacher/:id,
          // and teacher_subjects has no classId column — it never has. So the
          // class picker came up empty every time a class teacher was edited,
          // and the form looked as though they had no class at all.
          //
          // A class teacher is found the way the server finds them: the class
          // whose classMasterId is this user.
          const [subjectRows, classRows] = await Promise.all([
            fetchJson(`/api/teacher-subjects/teacher/${userId}`).catch(() => []),
            fetchJson('/api/classes-lite').catch(() => []),
          ]);
          if (!cancelled) {
            const subjectIds = (Array.isArray(subjectRows) ? subjectRows : [])
              .map(a => a.subjectId || a.Subject?.id).filter(Boolean);
            const myClasses = (Array.isArray(classRows) ? classRows : (classRows?.classes || []))
              .filter(c => String(c?.classMasterId || '') === String(userId))
              .map(c => c.id)
              .filter(Boolean);

            if (subjectIds.length) setSelectedSubjectIds(subjectIds);
            if (myClasses.length) setSelectedClassIds([myClasses[0]]);

            // Which kind of teacher this already IS decides which picker opens.
            // Without it the type stayed on its 'one_class' default, so a
            // subject teacher's subjects were loaded and then never shown.
            if (myClasses.length) setTeacherAllocationType('one_class');
            else if (subjectIds.length) setTeacherAllocationType('subjects');
          }
        }
        if (isParentRole) {
          if (Array.isArray(userData.linkedStudentIds)) {
            const prefetchedIds = userData.linkedStudentIds.map((id) => Number(id)).filter(Boolean);
            if (!cancelled) setSelectedStudentIds(prefetchedIds);
          } else {
            const data = await fetchJson(`/api/guardians?userId=${userId}`);
            if (!cancelled) {
              const rows = Array.isArray(data) ? data : (data?.rows || []);
              const studentIds = rows.map(g => g.studentId || g.Student?.id).filter(Boolean);
              setSelectedStudentIds(studentIds);
              // Deliberately NOT seeding selectedClassIdForStudents: with no
              // class chosen the list shows every student, so the linked
              // children are all visible and ticked. Picking one for them would
              // hide any sibling in another class.
            }
          }
        }
      } catch (e) {
        console.warn('Failed to load existing allocations for edit:', e?.message || e);
      }
    };
    loadExistingAllocations();
    return () => { cancelled = true; };
  }, [isEditing, userId, userData.roleId, userData.linkedStudentIds, isTeachingRole, isParentRole]);

  useEffect(() => {
    let cancelled = false;
    const loadAllocationsData = async () => {
      try {
        if (!isTeachingRole) return;
        const needSubjects = !(isNurPriTeacher && teacherAllocationType === 'one_class');
        const needClasses = showClassAllocations || shouldLoadClassesForTeacher;
        if ((!needSubjects || subjects.length) && (!needClasses || classes.length)) return;
        
        const ts = Date.now();
        const withTimeout = async (path, ms = 5000) => {
          const ctrl = new AbortController();
          const t = setTimeout(() => ctrl.abort(), ms);
          try {
            return await fetchJson(`${path}${path.includes('?') ? '&' : '?'}_ts=${ts}`, { signal: ctrl.signal });
          } finally { clearTimeout(t); }
        };

        let subjRes = needSubjects
          ? await Promise.allSettled([withTimeout('/api/allocations/subjects', 5000)]).then(r => r[0])
          : { status: 'fulfilled', value: [] };
        let classRes = needClasses
          ? await Promise.allSettled([withTimeout('/api/allocations/classes', 5000)]).then(r => r[0])
          : { status: 'fulfilled', value: [] };

        if (needSubjects && !(subjRes.status === 'fulfilled')) {
          subjRes = await Promise.resolve({ status: 'fulfilled', value: await withTimeout('/api/subjects', 7000) });
        }
        if (needClasses && !(classRes.status === 'fulfilled')) {
          classRes = await Promise.resolve({ status: 'fulfilled', value: await withTimeout('/api/classes', 7000) });
        }
        
        if (!cancelled) {
          const subj = subjRes.status === 'fulfilled' ? (subjRes.value || []) : [];
          const cls  = classRes.status === 'fulfilled' ? (classRes.value || []) : [];
          if (needSubjects) setSubjects(Array.isArray(subj) ? subj : []);
          if (needClasses) setClasses(Array.isArray(cls) ? cls : []);
        }
      } catch (e) {
        console.warn('Allocations data load skipped/failed:', e?.message || e);
      }
    };
    loadAllocationsData();
    return () => { cancelled = true; };
  }, [isTeachingRole, showClassAllocations, shouldLoadClassesForTeacher, isNurPriTeacher, teacherAllocationType, subjects.length, classes.length]);

  const finalizeSubmit = async (formData) => {
    let effectiveUserId = null;
    try {
      setSaving(true);
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing ? `/api/users/${userId}` : '/api/users';
      // THE ALLOCATION TRAVELS WITH THE USER NOW.
      //
      // It used to be two follow-up HTTP calls made after the user existed, and
      // the server discarded anything it did not recognise — so a class or a
      // list of subjects sent here simply vanished, and the failures below were
      // appended to a GREEN SUCCESS TOAST. "I assigned a class and it didn't
      // work" was exactly that.
      //
      // /api/users now reads classId and subjectIds and writes them in the same
      // transaction as the user, so a refused allocation refuses the whole save
      // and says why, instead of leaving a half-made teacher behind.
      //
      // The two are alternatives, never both: a nursery/primary teacher is a
      // class teacher OR a subject teacher. `teacherAllocationType` is the radio
      // the admin actually chose.
      const oneClassTeacher = isNurPriTeacher && teacherAllocationType === 'one_class';
      const allocation = !isTeachingRole ? {} : (oneClassTeacher
        ? { classId: selectedClassIds[0] ? Number(selectedClassIds[0]) : null, subjectIds: [] }
        : { classId: null, subjectIds: selectedSubjectIds.map(Number).filter(Boolean) });

      const payload = {
        ...formData,
        ...(isParentRole ? { linkedStudentIds: selectedStudentIds } : {}),
        ...allocation
      };

      const savedUser = await fetchJson(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      effectiveUserId = isEditing ? userId : (savedUser?.id || savedUser?.user?.id);

      const warnings = [];

      if (effectiveUserId && formData && formData.permissions && Object.keys(formData.permissions).length) {
        try {
          await fetchJson(`/api/users/${effectiveUserId}/permissions`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ permissions: formData.permissions })
          });
        } catch (e) {
          warnings.push('User was created, but permission assignment failed.');
        }
      }

      // The three allocation blocks that stood here are gone. Two are now part
      // of the /api/users payload above; the third posted a `teacherId` to
      // /api/class-subjects, a column that table does not have, behind a flag
      // (`showClassAllocations`) hard-coded to false — it could never have run,
      // and could not have worked if it had.

      const baseMessage = isEditing ? 'User updated successfully!' : 'User created successfully!';
      const message = warnings.length ? `${baseMessage} ${warnings.join(' ')}` : baseMessage;
      return { success: true, message };
    } catch (error) {
      console.error('User save error:', error);

      // THE SERVER'S OWN SENTENCE FIRST. It now refuses an allocation with a
      // message naming the actual conflict — which class the teacher already
      // runs, or that a nursery/primary teacher cannot hold both a class and
      // subjects. Every guess below is worse than that, and the 409 branch in
      // particular would report a genuine allocation clash as a duplicate email.
      const fromServer = String(error?.data?.error || '').trim();
      if (fromServer) return { success: false, message: fromServer };

      let errorMessage = 'Failed to save user. Please try again.';
      if (error.message && (error.message.includes('Email already in use') || error.message.includes('HTTP 409'))) {
        errorMessage = 'This email address is already registered. Please use a different email.';
      } else if (error.message && error.message.includes('HTTP 400')) {
        errorMessage = 'Invalid data provided. Please check your input and try again.';
      } else if (error.message && error.message.includes('HTTP 500')) {
        errorMessage = 'Server error occurred. Please try again.';
      }
      return { success: false, message: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  const handleStepOneSubmit = async (formData) => {
    setUserData(formData);
    setActiveStep(1);
  };

  const AllocationPicker = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('users.allocations.title')}</Typography>
      
      {isParentRole && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('users.allocations.selectClass')}</Typography>
                <TextField
                  select
                  size="small"
                  value={selectedClassIdForStudents}
                  onChange={(e) => setSelectedClassIdForStudents(e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="">{t('common.allClasses')}</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </TextField>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('users.allocations.selectStudents')}</Typography>
          {loadingStudents ? (
            <CircularProgress size={24} />
          ) : (
            <>
              {!students.length && <Alert severity="info">{t('users.allocations.noStudentsFound')}</Alert>}
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {students.map(s => {
                    const checked = selectedStudentIds.includes(s.id);
                    return (
                      <ListItem key={s.id} button onClick={() => {
                        setSelectedStudentIds(prev => checked ? prev.filter(id => id !== s.id) : [...prev, s.id]);
                      }}>
                        <ListItemIcon><Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple /></ListItemIcon>
                        <ListItemText 
                          primary={s.name || `${s.firstName} ${s.lastName}`} 
                          secondary={`${t('common.class')}: ${s.Class?.name || '-'}`} 
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </>
          )}
        </Box>
      )}

      {isNurPriTeacher && (
        <Box sx={{ mb: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">{t('users.allocations.teacherType')}</FormLabel>
            <RadioGroup row value={teacherAllocationType} onChange={(e) => setTeacherAllocationType(e.target.value)}>
              <FormControlLabel value="one_class" control={<Radio />} label={t('users.allocations.oneClassTeacher')} />
              <FormControlLabel value="subjects" control={<Radio />} label={t('users.allocations.subjectTeacher')} />
            </RadioGroup>
          </FormControl>
        </Box>
      )}

      <Grid container spacing={2}>
        {(!isNurPriTeacher || teacherAllocationType === 'subjects') && isTeachingRole && (
          <Grid item xs={12} md={showClassAllocations ? 6 : 12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('users.allocations.subjects')}</Typography>
            {!subjects.length && <Alert severity="info" sx={{ mb: 1 }}>{t('users.allocations.noSubjects')}</Alert>}
            <Paper variant="outlined">
              <List dense>
                {subjects.map(s => {
                  const checked = selectedSubjectIds.includes(s.id);
                  // THE LEVEL IS PART OF WHICH SUBJECT THIS IS.
                  //
                  // A school runs Mathematics at four levels and they are four
                  // different subject rows with four different sets of teachers.
                  // This list showed the name and the CATEGORY, so allocating a
                  // teacher meant choosing between several identical-looking
                  // "Mathematics" entries with nothing to tell them apart — and
                  // picking the wrong one puts a teacher on the wrong classes.
                  //
                  // The class list directly below has always shown `c.level`.
                  // This is the same fact, for the thing it matters more for.
                  const level = levelLabel(s.level);
                  const detail = [level, s.categoryName].filter(Boolean).join(' · ');
                  return (
                    <ListItem key={s.id} button onClick={() => setSelectedSubjectIds(prev => checked ? prev.filter(id => id !== s.id) : [...prev, s.id])}>
                      <ListItemIcon><Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple /></ListItemIcon>
                      <ListItemText
                        primary={s.name || s.title || `Subject ${s.id}`}
                        secondary={detail || undefined}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>
        )}

        {(showClassAllocations || (isNurPriTeacher && teacherAllocationType === 'one_class')) && isTeachingRole && (
          <Grid item xs={12} md={showClassAllocations ? 6 : 12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('users.allocations.classes')}</Typography>
            {!classes.length && <Alert severity="info" sx={{ mb: 1 }}>{t('users.allocations.noClasses')}</Alert>}
            <Paper variant="outlined">
              <List dense>
                {visibleClasses.map(c => {
                  const checked = selectedClassIds.includes(c.id);
                  const toggle = () => {
                    if (isNurPriTeacher && teacherAllocationType === 'one_class') {
                      setSelectedClassIds(prev => checked ? [] : [c.id]);
                    } else {
                      setSelectedClassIds(prev => checked ? prev.filter(id => id !== c.id) : [...prev, c.id]);
                    }
                  };
                  return (
                    <ListItem key={c.id} button onClick={toggle}>
                      <ListItemIcon><Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple /></ListItemIcon>
                      <ListItemText primary={c.name || c.className || `Class ${c.id}`} secondary={c.level || ''} />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="/user-list">{t('users.title')}</Link>
        <Typography color="text.primary">{isEditing ? t('users.editUser') : t('users.addUser')}</Typography>
      </Breadcrumbs>
      
      <Typography variant="h4" gutterBottom>{isEditing ? t('users.editUser') : t('users.addUser')}</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={60} /></Box>
      ) : (
        <Paper sx={{ p: 4 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>

          {activeStep === 0 && (
            <UserForm 
              initialData={userData} 
              roles={roles}
              isEditing={isEditing}
              onSubmit={handleStepOneSubmit}
              onCancel={() => navigate('/user-list')}
              saving={saving}
            />
          )}

          {(isTeachingRole || isParentRole) && activeStep === 1 && (
            <>
              <AllocationPicker />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button variant="outlined" onClick={() => setActiveStep(0)} disabled={saving}>{t('common.back')}</Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(2)}
                  disabled={
                    saving
                    || (isTeachingRole && !isEditing && isNurPriTeacher && teacherAllocationType === 'one_class' && selectedClassIds.length !== 1)
                    || (isTeachingRole && !isEditing && isNurPriTeacher && teacherAllocationType === 'subjects' && selectedSubjectIds.length === 0)
                    || (isParentRole && !isEditing && selectedStudentIds.length === 0)
                  }
                >
                  {t('common.next')}
                </Button>
              </Box>
            </>
          )}

          {((!isTeachingRole && !isParentRole && activeStep === 1) || ((isTeachingRole || isParentRole) && activeStep === 2)) && (
            <>
              <Typography variant="h6" gutterBottom>{t('common.review')}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1"><strong>{t('users.form.fullName')}:</strong> {userData.name}</Typography>
                <Typography variant="body1"><strong>{t('users.form.email')}:</strong> {userData.email}</Typography>
                <Typography variant="body1"><strong>{t('users.form.phone')}:</strong> {userData.phone || '-'}</Typography>
                <Typography variant="body1"><strong>{t('users.form.role')}:</strong> {roles.find(r => r.id === userData.roleId)?.name || '-'}</Typography>
                <Typography variant="body1"><strong>{t('users.form.status')}:</strong> {userData.status}</Typography>
                
                {isTeachingRole && (
                  <>
                    {isNurPriTeacher ? (
                      <>
                        <Typography variant="body1" sx={{ mt: 1 }}><strong>{t('users.allocations.teacherType')}:</strong> {teacherAllocationType === 'one_class' ? t('users.allocations.oneClassTeacher') : t('users.allocations.subjectTeacher')}</Typography>
                        {teacherAllocationType === 'one_class' ? (
                          <Typography variant="body1"><strong>{t('users.allocations.class') || 'Class'}:</strong> {classes.find(c => String(c.id) === String(selectedClassIds[0]))?.name || '-'}</Typography>
                        ) : (
                          <Typography variant="body1"><strong>{t('users.allocations.subjects') || 'Subjects'}:</strong> {subjects.filter(s=>selectedSubjectIds.includes(s.id)).map(s=>s.name).join(', ') || '-'}</Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography variant="body1" sx={{ mt: 1 }}><strong>{t('users.allocations.subjects') || 'Subjects'}:</strong> {subjects.filter(s=>selectedSubjectIds.includes(s.id)).map(s=>s.name).join(', ') || '-'}</Typography>
                        {showClassAllocations && (
                          <Typography variant="body1"><strong>{t('users.allocations.classes') || 'Classes'}:</strong> {classes.filter(c=>selectedClassIds.includes(c.id)).map(c=>c.name).join(', ') || '-'}</Typography>
                        )}
                      </>
                    )}
                  </>
                )}
                {isParentRole && (
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    <strong>{t('users.allocations.linkedStudents')}:</strong> {students.filter(s => selectedStudentIds.includes(s.id)).map(s => s.name || `${s.firstName} ${s.lastName}`).join(', ') || '-'}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button variant="outlined" onClick={() => setActiveStep((isTeachingRole || isParentRole) ? 1 : 0)} disabled={saving}>{t('common.back')}</Button>
                <Button 
                  variant="contained" 
                  onClick={async () => {
                    const result = await finalizeSubmit(userData);
                    if (result.success) {
                      enqueueSnackbar(result.message, { variant: 'success' });
                      navigate('/user-list');
                    } else {
                      enqueueSnackbar(result.message, { variant: 'error' });
                      setActiveStep(0);
                      setUserData(prev => ({ ...prev, error: result.message }));
                    }
                  }}
                  disabled={saving}
                >
                  {isEditing ? t('common.saveChanges') : t('users.createUser')}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default UserFormPage;
